import { useCallback, useEffect, useState } from 'react';
import { getErrorMessage } from '../../lib/errors';
import type { BrandSuggestionAcceptRead, BrandSuggestionRead } from '../../types/api';
import {
  acceptBrandSuggestion,
  dismissBrandSuggestion,
  listBrandSuggestions,
} from './brandMemoryApi';
import { publishBrandIdentity } from './useBrandIdentity';

export interface BrandSuggestionsResult {
  /** Pending suggestions, newest first. */
  items: BrandSuggestionRead[];
  isLoading: boolean;
  /** Load error only. Accept and dismiss throw to the caller instead. */
  error: string | null;
  refresh: () => void;
  /**
   * Applies the suggestion to the brand identity. On success the card leaves
   * the list and every mounted brand-identity reader gets the new record.
   */
  accept: (suggestion: BrandSuggestionRead) => Promise<BrandSuggestionAcceptRead>;
  dismiss: (suggestion: BrandSuggestionRead) => Promise<BrandSuggestionRead>;
}

/** Result of the last completed load, tagged with the business it belongs to. */
interface LoadedSuggestions {
  businessId: string;
  items: BrandSuggestionRead[];
  error: string | null;
}

/** Stable empty result, so consumers do not see a new array identity per render. */
const NO_ITEMS: never[] = [];

/**
 * The brand's pending suggestions: rules for the brand identity that Autopostmate
 * proposes and the owner decides on. Loaded rows are tagged with their
 * business and the visible state is derived during render, like the other
 * knowledge-base hooks, so another brand's suggestions never show.
 */
export function useBrandSuggestions(businessId: string | null): BrandSuggestionsResult {
  const [loaded, setLoaded] = useState<LoadedSuggestions | null>(null);
  const [nonce, setNonce] = useState(0);

  const refresh = useCallback(() => setNonce((value) => value + 1), []);

  useEffect(() => {
    if (!businessId) return;
    const controller = new AbortController();
    let cancelled = false;

    void (async () => {
      try {
        const items = await listBrandSuggestions(businessId, controller.signal);
        if (cancelled) return;
        setLoaded({ businessId, items, error: null });
      } catch (caught) {
        if (cancelled || controller.signal.aborted) return;
        setLoaded({
          businessId,
          items: [],
          error: getErrorMessage(caught, 'Could not load suggested changes.'),
        });
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [businessId, nonce]);

  const drop = useCallback((business: string, id: string) => {
    setLoaded((current) =>
      current !== null && current.businessId === business
        ? { ...current, items: current.items.filter((item) => item.id !== id) }
        : current,
    );
  }, []);

  const accept = useCallback(
    async (suggestion: BrandSuggestionRead) => {
      if (!businessId) throw new Error('No brand is selected.');
      const result = await acceptBrandSuggestion(businessId, suggestion.id);
      drop(businessId, suggestion.id);
      publishBrandIdentity(result.brand_identity);
      return result;
    },
    [businessId, drop],
  );

  const dismiss = useCallback(
    async (suggestion: BrandSuggestionRead) => {
      if (!businessId) throw new Error('No brand is selected.');
      const result = await dismissBrandSuggestion(businessId, suggestion.id);
      drop(businessId, suggestion.id);
      return result;
    },
    [businessId, drop],
  );

  const isCurrent = loaded !== null && loaded.businessId === businessId;

  return {
    items: isCurrent ? loaded.items : NO_ITEMS,
    isLoading: businessId !== null && !isCurrent,
    error: isCurrent ? loaded.error : null,
    refresh,
    accept,
    dismiss,
  };
}
